import { useCallback, useEffect, useRef } from 'react';

import { useSwipe } from '../hooks/useSwipe';
import { classFor } from '../lib/class-map';
import { cn } from '../lib/cn';

import { Button } from './Button';
import { IconButton } from './IconButton';
import type { ToastDismissReason, ToastRecord, ToastTone } from './toast-context';
import s from './ToastItem.module.css';
import { TONE_ICON_PATHS, TONE_ROLE } from './tone-icons';

interface ToastItemProps {
  toast: ToastRecord;
  onDismiss: (id: string, reason: ToastDismissReason) => void;
}

const TONE_CLASS: Record<ToastTone, string> = {
  success: s.success ?? '',
  error: s.error ?? '',
  warning: s.warning ?? '',
  info: s.info ?? '',
};

export function ToastItem({ toast, onDismiss }: ToastItemProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const { id, message, tone, action, duration } = toast;

  // The timer lives in refs so hover/focus can pause it without a render:
  // `remaining` is what is left, `startedAt` is when the current run began.
  const remaining = useRef(duration ?? 0);
  const startedAt = useRef(0);
  const timer = useRef<number | null>(null);

  const clear = useCallback((): void => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const start = useCallback((): void => {
    if (duration === null) return;
    clear();
    startedAt.current = Date.now();
    timer.current = window.setTimeout(() => {
      onDismiss(id, 'timeout');
    }, remaining.current);
  }, [duration, clear, id, onDismiss]);

  const pause = useCallback((): void => {
    if (duration === null || timer.current === null) return;
    remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
    clear();
  }, [duration, clear]);

  useEffect(() => {
    start();
    return clear;
  }, [start, clear]);

  useSwipe(ref, {
    axis: 'x',
    direction: 'both',
    onSwipe: () => {
      onDismiss(id, 'user');
    },
  });

  return (
    <div
      ref={ref}
      role={classFor(TONE_ROLE, tone)}
      className={cn(s.root, classFor(TONE_CLASS, tone))}
      onPointerEnter={pause}
      onPointerLeave={start}
      onFocus={pause}
      onBlur={start}
    >
      <svg
        className={s.icon}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={classFor(TONE_ICON_PATHS, tone)} />
      </svg>
      <p className={s.message}>{message}</p>
      {action !== undefined ? (
        <Button
          variant="ghost"
          size="sm"
          className={s.action}
          onClick={() => {
            action.onClick();
            onDismiss(id, 'action');
          }}
        >
          {action.label}
        </Button>
      ) : null}
      <IconButton
        label="Dismiss"
        className={s.dismiss}
        onClick={() => {
          onDismiss(id, 'user');
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </IconButton>
    </div>
  );
}
